import { useState, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';

export interface NotificationOptions {
  body?: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
}

export const useNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    
    if (permission === 'granted') return permission;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }, [permission]);

  const notify = useCallback((title: string, options?: NotificationOptions) => {
    if (typeof Notification === 'undefined' || permission !== 'granted') {
      toast(title, {
        icon: '🔔',
        duration: 4000,
      });
      return null;
    }
    
    try {
      const notification = new Notification(title, {
        icon: options?.icon || '/favicon.ico',
        ...options,
      });
      
      const bell = new Audio('/sounds/bell.mp3');
      bell.play().catch((error) => {
        console.warn('Bell sound failed to play:', error);
      });
      
      return notification;
    } catch (error) {
      console.error('Error creating notification:', error);
      toast(title, {
        icon: '🔔',
        duration: 4000,
      });
      return null;
    }
  }, [permission]);

  return { permission, requestPermission, notify };
};
